// src/utils/formatters.js

/**
 * Format a wallet address for display by truncating the middle
 * @param {string} address - Full wallet address
 * @param {number} startLength - Number of characters to show at start (default: 6)
 * @param {number} endLength - Number of characters to show at end (default: 4)
 * @returns {string} Formatted wallet address (e.g., "0x1234...5678")
 */
export const formatWalletAddress = (address, startLength = 6, endLength = 4) => {
  if (!address) return '';
  if (address.length <= startLength + endLength) return address;
  
  const start = address.substring(0, startLength);
  const end = address.substring(address.length - endLength);
  
  return `${start}...${end}`;
};

/**
 * Format a date for display
 * @param {string|Date} date - Date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) return '';
    
    // Default options
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    };
    
    return new Intl.DateTimeFormat('en-US', defaultOptions).format(dateObj);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format a number for display with commas and optional decimal places
 * @param {number|string} value - Number to format
 * @param {number} decimals - Number of decimal places to show (default: 2)
 * @param {boolean} addCurrency - Whether to add currency symbol (default: false)
 * @param {string} currencySymbol - Currency symbol to use (default: '$')
 * @returns {string} Formatted number
 */
export const formatNumber = (value, decimals = 2, addCurrency = false, currencySymbol = '$') => {
  if (value === null || value === undefined || value === '') return '';
  
  try {
    // Convert to float
    const num = parseFloat(value);
    
    // Check if it's a valid number
    if (isNaN(num)) return '';
    
    // Format with commas and specified decimal places
    const formattedNum = num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    
    return addCurrency ? `${currencySymbol}${formattedNum}` : formattedNum;
  } catch (error) {
    console.error('Error formatting number:', error);
    return '';
  }
};

export default {
  formatWalletAddress,
  formatDate,
  formatNumber
};
